/* eslint-disable @typescript-eslint/no-unsafe-return */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserLevel } from '../enums/user-level.enum';
import { UserTitle } from '../enums/user-title.enum';
import { UserDeserialize } from '../models/user/user.deserialize';
import { User, UserDetails } from '../models/user/user.model';
import { UserSerialize } from '../models/user/user.serialize';
import { isValidDateInDdMmYyyyFormat } from '../utilities/date-validator';
import { DialogService } from './dialog.service';

const importedDateFields = [
  [
    'anzianitaPubblicaAmministrazione',
    'Anzianità nella pubblica amministrazione',
  ],
  ['dataAssunzioneInEnac', 'Data assunzione in ENAC'],
  ['anzianitaCategoriaAttuale', 'Anzianità nella categoria attuale'],
  ['anzianitaLivelloAttuale', 'Anzianità nel livello attuale'],
] as const;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);

  constructor(private httpClient: HttpClient) {}

  getUsers() {
    return this.httpClient
      .get<any>(`${environment.apiUrl}/admin/users`)
      .pipe(
        map((response) =>
          response.result.map((user: any) => UserDeserialize.toTableUser(user)),
        ),
      );
  }

  new(userDetails: UserDetails) {
    this.httpClient
      .post<any>(
        `${environment.apiUrl}/admin/users`,
        UserSerialize.toUserDetails(userDetails),
      )
      .pipe(
        tap((response) => {
          this.dialogService.showSuccess(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.NEW_SUCCESSFUL',
            ),
          );
        }),
        catchError((error) => {
          this.dialogService.showError('ADMIN_PAGE.DIALOG_SERVICE.NEW_ERROR');
          return of(null);
        }),
      )
      .subscribe();
  }

  validate(users: any): { isValid: boolean; errors: string[] } {
    let isValid: boolean = true;
    let errors: string[] = [];
    users.forEach((user: any, index: number) => {
      if (!Object.values(UserTitle).includes(user.title)) {
        isValid = false;
        errors.push(`Titolo ${user.title} non valido.\n`);
      }
      if (!Object.values(UserLevel).includes(user.level)) {
        isValid = false;
        errors.push(`Livello ${user.level} non valido.\n`);
      }
      importedDateFields.forEach(([fieldName, label]) => {
        if (!isValidDateInDdMmYyyyFormat(user[fieldName])) {
          isValid = false;
          errors.push(
            `Riga ${index + 1}: ${label} deve essere una data valida nel formato gg/mm/aaaa.\n`,
          );
        }
      });
    });
    return { isValid: isValid, errors: errors };
  }

  import(users: any) {
    this.httpClient
      .post<any>(`${environment.apiUrl}/admin/batch`, { data: users })
      .pipe(
        tap((response) => {
          this.dialogService.showSuccess(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.IMPORT_SUCCESSFUL',
            ),
          );
        }),
        catchError((error) => {
          this.dialogService.showError(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.IMPORT_ERROR',
            ),
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  update(user: User) {
    if (user.details) {
      this.httpClient
        .put<any>(
          `${environment.apiUrl}/admin/users/${user.id}`,
          UserSerialize.toUserDetails(user.details),
        )
        .pipe(
          tap((response) => {
            this.dialogService.showSuccess(
              this.translateService.instant(
                'ADMIN_PAGE.DIALOG_SERVICE.UPDATE_SUCCESSFUL',
              ),
            );
          }),
          catchError((error) => {
            this.dialogService.showError(
              this.translateService.instant(
                'ADMIN_PAGE.DIALOG_SERVICE.UPDATE_ERROR',
              ),
            );
            return of(null);
          }),
        )
        .subscribe();
    }
  }

  delete(userId: string) {
    this.httpClient
      .delete<any>(`${environment.apiUrl}/admin/users/${userId}`)
      .pipe(
        tap((response) => {
          this.dialogService.showSuccess(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.DELETE_SUCCESSFUL',
            ),
          );
        }),
        catchError((error) => {
          this.dialogService.showError(
            'ADMIN_PAGE.DIALOG_SERVICE.DELETE_ERROR',
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  deleteAll() {
    this.httpClient
      .delete<any>(`${environment.apiUrl}/admin/users`)
      .pipe(
        tap((response) => {
          this.dialogService.showSuccess(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.DELETE_ALL_SUCCESSFUL',
            ),
          );
        }),
        catchError((error) => {
          this.dialogService.showError(
            'ADMIN_PAGE.DIALOG_SERVICE.DELETE_ALL_ERROR',
          );
          return of(null);
        }),
      )
      .subscribe();
  }
}
